import { useEffect } from 'react';
import { useLocation } from '@react-router';
import type { GetContactCollectionByMagicLinkQuery } from '../../../../admin/contactCollector/api/ContactCollector.graphql.generated';
import { useGetDataForContactCollectionGuestQuery } from '../../../../admin/contactCollector/api/ContactCollector.graphql.generated';
import { Category } from '@graphql/generated-base';
import { useLoadGoogleFont } from '@shared/hooks/useLoadGoogleFont';
import { useRouterHelper, useTranslation } from '@shared/core';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { useContactCollectorGuestTelemetry } from '../../ContactCollectorGuest.telemetry';
import { withWindow } from '@shared/utils/withWindow';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useEventLevelDateDisplayVariants } from '@shared/utils/eventDateDisplay/useEventLevelDateDisplay';
import { contactCollectorGuestRoutePaths } from '../../contactCollectorGuest.routes';
import { CONTACT_COLLECTOR_UTM } from '../../contactCollector.constants';
import type { Context } from '../../shared/types';

export const useLandingController = (setContext: React.Dispatch<React.SetStateAction<Context>>, data?: GetContactCollectionByMagicLinkQuery) => {
  const routeHelpers = useRouterHelper();
  const location = useLocation();
  const isMobile = useIsMobileScreen();
  const { trackError, pageViewed, buttonInteracted } = useContactCollectorGuestTelemetry();
  const {
    data: queryData,
    loading,
    error,
  } = useGetDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: data?.getContactCollectionByMagicLink?.eventId ?? '',
    },
    skip: !data?.getContactCollectionByMagicLink?.eventId,
    onError: error => trackError('Query', error),
  });

  const eventTitleFont = queryData?.eventById?.eventDesign?.fonts?.find(font => font.category === Category.EVENT_TITLE)?.font?.fontFamily;
  const fontFamily = eventTitleFont ?? queryData?.eventById?.eventDesign?.font?.fontFamily ?? 'PT Serif';
  const fontTextTransform = eventTitleFont ? 'none' : (queryData?.eventById?.eventDesign?.font?.textTransform ?? 'none');
  useLoadGoogleFont([`${fontFamily}:400`]);
  const { tq } = useTranslation('contactCollector');
  const { formatDate } = useEventLocaleFormatter();
  const info = queryData?.eventById?.info;
  const eventDisplayName = info?.eventDisplayName || tq`The Couple`;
  // Multi-day event date (PRNT-4625): render a saved range (or custom text) when configured, else the single
  // date. Range start = the dedicated startDate override — never the event date (which sits within the event)
  // — so when startDate is null it falls back to single date.
  const { localized: eventDateRangeDisplay, isLoading: eventDateRangeLoading } = useEventLevelDateDisplayVariants({
    startTimestamp: info?.startDate?.timestamp,
    timezone: info?.startDate?.timezone,
    dateMetadata: info?.dateMetadata,
    endDateTimestamp: info?.endDate?.timestamp,
  });
  // Format the event date client-side in the event locale: the BE's `dateString` is always rendered in
  // English, so a Japanese-locale landing showed "Sunday, May 31, 2026" next to fully translated copy.
  // Same source (`dateV0918`) and same parts as the previous `dateFormat: full` string minus the time —
  // `Intl` reorders them per locale on its own.
  const singleDate = formatDate(info?.dateV0918?.timestamp, 'weekday, MMMM d, yyyy', { titlecase: true, timezone: info?.dateV0918?.timezone }) ?? '';
  const date = eventDateRangeDisplay ?? singleDate;
  const coupleNames = (info?.ownerFirstName && info?.fianceeFirstName ? tq`${info.ownerFirstName} & ${info.fianceeFirstName}` : info?.ownerFirstName) || tq`The Couple`;
  const website = queryData?.eventById?.website ?? '';

  // The page must not render ahead of the Context it writes. Gating only the persist below on the flag while
  // reporting `loading: false` would paint the single-date fallback and a live "Provide Details" CTA against a
  // still-blank Context: a range event would flash the wrong date, and a guest who tapped through in that
  // window would land on /form, which redirects straight back here on the empty `coupleNames` default. One
  // value drives both, so render and persist can't disagree. The remote flag provider always settles loading
  // (even on fetch error), so this can't hang.
  const isLoading = loading || eventDateRangeLoading;

  useEffect(() => {
    if (!isLoading) {
      pageViewed.enter('landingPage', { eventHandle: website });
      setContext({
        coupleNames,
        eventDisplayName,
        date,
        eventDateDisplayEnabled: !!data?.getContactCollectionByMagicLink?.eventDateDisplayEnabled,
        website,
        websiteLinkEnabled: !!data?.getContactCollectionByMagicLink?.websiteLinkEnabled,
        fontFamily,
        fontTextTransform,
        mediaType: data?.getContactCollectionByMagicLink?.mediaType ?? '',
        mediaSource: data?.getContactCollectionByMagicLink?.mediaSource ?? '',
        firebaseId: queryData?.eventById?.firebaseId ?? '',
      });
    }
  }, [
    coupleNames,
    data?.getContactCollectionByMagicLink?.eventDateDisplayEnabled,
    data?.getContactCollectionByMagicLink?.websiteLinkEnabled,
    date,
    eventDisplayName,
    pageViewed,
    isLoading,
    setContext,
    website,
    fontFamily,
    fontTextTransform,
    data?.getContactCollectionByMagicLink?.mediaType,
    data?.getContactCollectionByMagicLink?.mediaSource,
    queryData?.eventById?.firebaseId,
  ]);

  const handleOnVisitEventWebsiteClick = () => {
    buttonInteracted({ buttonType: 'navigation', element: 'visitEventWebsite' });
    withWindow(() => {
      window.open(`${window.location.origin}/${website}?${CONTACT_COLLECTOR_UTM}`, '_blank');
    });
  };

  const handleOnProvideDetailsClick = () => {
    buttonInteracted({ buttonType: 'navigation', element: 'provideDetails' });
    routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.form.path, location.search);
  };

  return {
    fontFamily,
    fontTextTransform,
    eventDisplayName,
    coupleNames,
    date,
    loading: isLoading,
    error,
    handleOnProvideDetailsClick,
    handleOnVisitEventWebsiteClick,
    isMobile,
  };
};
